import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";
import { SafeImage } from "@/components/site/SafeImage";
import { cn } from "@/lib/utils";

interface PropertyGalleryProps {
  images: string[];
  alt: string;
  /** Listing tag (For Sale / For Rent / Off-Plan) shown on the main photo. */
  tag?: string;
}

export function PropertyGallery({ images, alt, tag }: PropertyGalleryProps) {
  const [active, setActive] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const safe = images.filter(Boolean);
  if (safe.length === 0) return null;

  const openAt = (i: number) => setLightboxIndex(i);
  const close = () => setLightboxIndex(null);

  return (
    <>
      <div className="grid gap-2 sm:gap-3 md:grid-cols-12">
        <div className="md:col-span-8">
          <button
            type="button"
            onClick={() => openAt(active)}
            aria-label="Open gallery"
            className="group relative block aspect-[4/3] w-full overflow-hidden bg-[var(--surface-2)] sm:aspect-[16/9]"
          >
            <SafeImage
              src={safe[active] ?? safe[0]}
              alt={alt}
              loading="eager"
              fetchPriority="high"
              sizes="(min-width: 768px) 66vw, 100vw"
              widths={[640, 960, 1280, 1600]}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            />
            {tag && (
              <span className="pointer-events-none absolute left-3 top-3 inline-flex items-center bg-white px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--navy)]">
                {tag.toUpperCase()}
              </span>
            )}
            <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1.5 bg-[rgba(13,20,34,0.78)] px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.18em] text-[var(--cream)] backdrop-blur-sm">
              <Maximize2 className="size-3" />
              View all · {safe.length}
            </span>
          </button>
        </div>
        {safe.length > 1 && (
          <div className="grid grid-cols-4 gap-2 sm:gap-3 md:col-span-4 md:h-full md:grid-cols-2 md:grid-rows-2">
            {safe.slice(1, 5).map((src, i) => {
              const idx = i + 1;
              const isLast = i === 3 && safe.length > 5;
              return (
                <button
                  key={src + idx}
                  type="button"
                  onClick={() => (isLast ? openAt(idx) : setActive(idx))}
                  className={cn(
                    "relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)] transition-all md:aspect-auto md:h-full md:min-h-0",
                    active === idx
                      ? "outline outline-1 outline-[var(--cream)]"
                      : "opacity-80 hover:opacity-100",
                  )}
                  aria-label={isLast ? `View all ${safe.length} photos` : `Show photo ${idx + 1}`}
                >
                  <SafeImage
                    src={src}
                    alt=""
                    loading="lazy"
                    sizes="(min-width: 768px) 16vw, 25vw"
                    widths={[240, 360, 480, 640]}
                    className="h-full w-full object-cover"
                  />
                  {isLast && (
                    <span className="absolute inset-0 flex items-center justify-center bg-[rgba(13,20,34,0.62)] text-[13px] font-medium text-[var(--cream)]">
                      +{safe.length - 4} more
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={safe}
          alt={alt}
          startIndex={lightboxIndex}
          onClose={close}
          onIndexChange={(i) => setActive(i)}
        />
      )}
    </>
  );
}

interface LightboxProps {
  images: string[];
  alt: string;
  startIndex: number;
  onClose: () => void;
  onIndexChange?: (i: number) => void;
}

function Lightbox({ images, alt, startIndex, onClose, onIndexChange }: LightboxProps) {
  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const total = images.length;

  const goto = useCallback(
    (next: number) => {
      const wrapped = ((next % total) + total) % total;
      setIndex(wrapped);
      onIndexChange?.(wrapped);
    },
    [total, onIndexChange],
  );

  const next = useCallback(() => goto(index + 1), [goto, index]);
  const prev = useCallback(() => goto(index - 1), [goto, index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, next, prev]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const node = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Property photos"
      data-on-dark
      className="fixed inset-0 z-[100] flex flex-col bg-[rgba(13,20,34,0.96)] backdrop-blur-sm"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-[rgba(255,255,255,0.08)] px-4 py-3 sm:px-6">
        <span className="text-[12px] uppercase tracking-[0.22em] text-[var(--cream)]">
          {index + 1} / {total}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="-mr-2 inline-flex size-11 items-center justify-center text-[var(--cream)] transition-colors hover:text-white"
        >
          <X className="size-5" />
        </button>
      </div>

      {/* Stage */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <SafeImage
          key={images[index]}
          src={images[index]}
          alt={`${alt} · photo ${index + 1}`}
          loading="eager"
          sizes="100vw"
          widths={[960, 1280, 1600, 2000]}
          className="max-h-full max-w-full object-contain"
        />

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous photo"
              className="absolute left-2 top-1/2 hidden -translate-y-1/2 items-center justify-center bg-[rgba(13,20,34,0.6)] p-3 text-[var(--cream)] transition-colors hover:bg-[var(--cream)] hover:text-[var(--navy)] sm:left-4 sm:inline-flex sm:p-4"
            >
              <ChevronLeft className="size-6" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next photo"
              className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center justify-center bg-[rgba(13,20,34,0.6)] p-3 text-[var(--cream)] transition-colors hover:bg-[var(--cream)] hover:text-[var(--navy)] sm:right-4 sm:inline-flex sm:p-4"
            >
              <ChevronRight className="size-6" />
            </button>
          </>
        )}
      </div>

      {/* Mobile arrows on bottom */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-3 border-t border-[rgba(255,255,255,0.08)] px-4 py-3 sm:hidden">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous photo"
            className="inline-flex size-11 items-center justify-center bg-[rgba(255,255,255,0.06)] text-[var(--cream)]"
          >
            <ChevronLeft className="size-5" />
          </button>
          <span className="px-3 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
            Swipe
          </span>
          <button
            type="button"
            onClick={next}
            aria-label="Next photo"
            className="inline-flex size-11 items-center justify-center bg-[rgba(255,255,255,0.06)] text-[var(--cream)]"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      )}

      {/* Thumb strip — desktop */}
      {total > 1 && (
        <div className="hidden border-t border-[rgba(255,255,255,0.08)] sm:block">
          <div className="container-wide overflow-x-auto py-3">
            <div className="flex gap-2">
              {images.map((src, i) => (
                <button
                  key={src + i}
                  type="button"
                  onClick={() => goto(i)}
                  aria-label={`Go to photo ${i + 1}`}
                  className={cn(
                    "relative h-12 w-[72px] flex-none overflow-hidden bg-[var(--surface-2)] transition-all md:h-14 md:w-20",
                    i === index ? "outline outline-2 outline-[var(--cream)]" : "opacity-60 hover:opacity-100",
                  )}
                >
                  <SafeImage
                    src={src}
                    alt=""
                    loading="lazy"
                    sizes="80px"
                    widths={[160, 240]}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
